import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import _ from 'lodash';
import { RootState } from '../app/store';
import { PassengerInterface } from '../models/passenger';
import {
  SeatInterface,
  SeatmapInterface,
  SeatmapModelInterface,
  SeatRowInterface,
} from '../models/seatmap';
import {
  getActiveFlightPaxes,
  getCurrentlySelectedPax,
  getPaxesEligibleForSeatChange,
} from '../utils/passengerHelper';
import ResourceService from '../services/resource/ResourceService';

export interface SeatmapState {
  model: SeatmapModelInterface;
  exitSeatModal: any;
}

const initialState: SeatmapState = {
  model: {
    excludeFromLocalStorage: ['apicall'],
    allSeatsRendered: false,
    route: 'ck_flightseatmap',
    flights: [],
    rows: {},
    fullCkiProcess: true,
    messages: {},
    seatmapForClass: 'Y',
    seatmapForOperatingType: 'ECONOMY_CLASS',
    chargeableSeatmap: false,
    priceInfo: {},
    seatAvailabilityInfo: {},
    currency: null,
    shoppingBasketData: null,
    seatmapFlightNotification: [],
    priceInfoModel: {},
    seatAvailabilityInfoModel: {},
    activeFlightId: 1,
    activeFlightIsChargeable: false,
    activePaxId: 1,
    rowsInfo: {
      maxSeatsPerRow: 6,
      activeSeatConfigIds: {},
      rowConfigs: {
        ECONOMY_CLASS: {
          M: { '1': { configId: 1, configHeaders: 'ABC DEF' } },
        },
      },
      seatsConfirmed: [],
      seatsToConfirm: {},
      blockedSeats: [],
      isRichSeatmap: false,
      seatmapClass: 'seatmap--seats-generic',
      hasSeatmapSubClass: false,
      hasAdditonalSubClass: false,
      disabledClass: undefined,
      classSeats: {
        business: '0',
        economy: '0',
        first: '0',
        premiumEconomy: '0',
      },
    },
    rowConfigs: [],
    rowsAsCollection: [],
    facilityCollection: {
      toilet: [],
      galley: [],
    },
  },
  exitSeatModal: {
    isOpen: false,
    seatToChoose: '',
  },
};

export const seatmapSlice = createSlice({
  name: 'seatmap',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetSeatmapModel: (state) => {
      return initialState;
    },
    setSeatmapModel: (state, action) => {
      state.model = action.payload;
      state.model.flights?.forEach((flight) => {
        flight.passengers?.forEach((pax) => {
          if (!pax.initialSeat) {
            pax.initialSeat = pax.referenceOrSeat || pax.selectedSeatReference || '';
          }
        });
      });
    },
    setSeatmapModelAttribute: (state, action: PayloadAction<{ key: string; value: any }>) => {
      const key = action.payload.key;
      const value = action.payload.value;
      Object.assign(state.model, { [key]: value });
    },
    setActivePax: (state, action: PayloadAction<number>) => {
      let paxid = action.payload;
      let activeFlightPaxes = getActiveFlightPaxes(state.model);
      if (_.isEmpty(activeFlightPaxes)) {
        state.model.activePaxId = paxid;
        return;
      }

      activeFlightPaxes.forEach((pax) => {
        pax.isActive = false;
        if (pax.paxIdReference === paxid) pax.isActive = true;
      });

      // just in case set the active pax id if someone decides to click pax out of order
      state.model.activePaxId = paxid;
    },
    changeSeatStatus: (state, action: PayloadAction<{ seat: SeatInterface; value: string }>) => {
      let seat = action.payload.seat;
      let value = action.payload.value;

      // First we need to find that seat in the model
      if (state.model.rowsAsCollection) {
        let seatRows = state.model.rowsAsCollection;
        if (!seatRows) {
          return;
        }
        let foundRow = _.find(seatRows, {
          rowPosition: seat.seatNumberDigit,
        }) as SeatRowInterface;
        if (!foundRow) {
          return;
        }
        let foundSeat = _.find(foundRow.seatArray, {
          seatNumber: seat.seatNumber,
        });
        // Then we update the data
        if (foundSeat) {
          foundSeat.computedStatus = value;
        }
      }
    },
    changePaxSeat: (state, action: PayloadAction<{ paxId: number; value: string }>) => {
      let paxId = action.payload.paxId;
      let value: string = action.payload.value;
      let activeFlightPaxes = getActiveFlightPaxes(state.model);
      let foundPax = _.find(activeFlightPaxes, { paxIdReference: paxId });
      if (foundPax) {
        foundPax.selectedSeatReference = value;
        foundPax.referenceOrSeat = value;
      }
    },
    setRowConfigs: (state) => {
      let model = state.model;
      let currentClass = model.rowsInfo.rowConfigs[model.seatmapForOperatingType];
      let rowConfigs: (string[] | null)[] = [];
      let rowConfigsUpper: (string[] | null)[] = [];
      // get the headers for the main deck
      _.each(currentClass.M, function (rowConfig) {
        let configHeaderArray = rowConfig.configHeaders.split(''); // ['A','B','C']
        let configHeaderDictArray = []; // [{0: 'A'},{0: 'B"},{ 0: 'C'}]
        for (let i = 0; i < configHeaderArray.length; i++) {
          configHeaderDictArray[i] = _.zipObject([0], _.trim(configHeaderArray[i]));
        }
        rowConfigs[rowConfig.configId] = configHeaderArray;
      });
      model.rowConfigs = rowConfigs;

      // and the upper deck
      // check if there is an upper deck first
      if (currentClass.U) {
        _.each(currentClass.U, function (rowConfig) {
          let configHeaderArray = rowConfig.configHeaders.split(''); // ['A','B','C']
          let configHeaderDictArray = []; // [{0: 'A'},{0: 'B"},{ 0: 'C'}]
          for (let i = 0; i < configHeaderArray.length; i++) {
            configHeaderDictArray[i] = _.zipObject([0], _.trim(configHeaderArray[i]));
          }
          rowConfigsUpper[rowConfig.configId] = configHeaderArray;
        });
      }
      model.rowConfigsUpper = rowConfigsUpper;
    },
    /**
     * Set if we're working with economy or business class, depending on the class of active pax.
     */
    setSeatmapWorkingState: (state) => {
      let model = state.model;
      let foundFlight = _.find(model.flights, { isActive: true });
      if (!foundFlight) {
        return;
      }
      let paxesForTransaction = foundFlight.passengers;
      let activePax = getCurrentlySelectedPax(paxesForTransaction);
      let seatmapMode = 'normal';
      let double = false;
      const activeSeatConfigId = model.rowsInfo.activeSeatConfigIds[model.activePaxId];
      let seatmapArray = [];
      let currentDeckUpper = false;
      let deck = !_.isUndefined(activePax) ? activePax.seatDeck : 'M';
      // If we failed to get the active passenger by status, just take the
      // first passenger on the flight for failsafe
      if (_.isUndefined(activePax) && paxesForTransaction.length > 0) {
        activePax = paxesForTransaction[0];
      }

      // Set the seatmap mode
      if (activePax && activePax.isDisabledOnSeatmap) {
        seatmapMode = 'disabled';
      } else if (activePax && activePax.infant) {
        seatmapMode = 'infant';
      } else if (activePax && activePax.passengerType === 'child') {
        seatmapMode = 'child';
      }

      let containsSpecificCssClass: boolean = false;
      if (model.rowsInfo.isRichSeatmap) {
        const specificRichSeatmapClasses: string[] = [
          'Seatmap-a320',
          'Seatmap-a321',
          'Seatmap-cs300',
          'Seatmap-cs100',
        ];

        containsSpecificCssClass = specificRichSeatmapClasses.some((className: string) =>
          model.rowsInfo.seatmapClass.includes(className)
        );
      }

      if (containsSpecificCssClass) {
        let mergedActiveSeatmap: any = [];
        let businessRows = 0;
        let economyRows = 0;

        _.forEach(model.rows, (seatRows, seatMapClass) => {
          let mappedArray: any[][] = mapSeatArray(seatRows[deck], activeSeatConfigId);

          if (mergedActiveSeatmap) {
            mergedActiveSeatmap = [...mergedActiveSeatmap, ...mappedArray];
          } else {
            mergedActiveSeatmap = mergedActiveSeatmap.concat(mappedArray);
          }

          if (seatMapClass === 'BUSINESS_CLASS') {
            businessRows = mappedArray.length;
          }

          if (seatMapClass === 'ECONOMY_CLASS') {
            economyRows = mappedArray.length;
          }

          if (model.seatmapForOperatingType !== seatMapClass) {
            model.rowsInfo.disabledClass = seatMapClass;
            // cut all business + 1 economy
            if (model.seatmapForOperatingType === 'BUSINESS_CLASS') {
              mergedActiveSeatmap = mergedActiveSeatmap.splice(0, businessRows + 1);
            }

            // cut the last row of business
            if (model.seatmapForOperatingType === 'ECONOMY_CLASS') {
              mergedActiveSeatmap = mergedActiveSeatmap.splice(-1);
            }
          }
        });
        model.rowsAsCollection = mergedActiveSeatmap;
      } else {
        let activeSeatmap: any = {};
        _.forEach(model.rows, (seatRows, seatMapClass) => {
          if (seatMapClass === model.seatmapForOperatingType) {
            activeSeatmap = seatRows;
          }
        });

        const config = ResourceService.getConfig();
        if (config.pages.seatmap.SEATMAP_MULTIPLE_COMPARTMENTS) {
          let rowsAsCollection: any = [];
          model.rowConfigs.forEach((rowConfig: any, rowConfigKey) => {
            rowsAsCollection[rowConfigKey] = mapSeatArray(activeSeatmap[deck], rowConfigKey);
          });
          model.rowsAsCollection = rowsAsCollection;
        } else {
          model.rowsAsCollection[activeSeatConfigId] = mapSeatArray(
            activeSeatmap[deck],
            activeSeatConfigId
          );
        }
      }

      let numberOfDecks = Object.keys(model.rows[model.seatmapForOperatingType]).length;
      if (numberOfDecks === 2) {
        double = true;
      }
      if (deck === 'U') {
        currentDeckUpper = true;
      }

      model.seatmapMode = seatmapMode;
      model.double = double;
      model.currentDeckUpper = currentDeckUpper;
    },

    processSeatmapCollection: (state) => {
      let model = state.model;
      let data = getSeatmapDataObject(model);
      let seatmapCollection = model.rowsAsCollection;
      model.rowConfigs.forEach((rowConfig: any, rowConfigKey) => {
        seatmapCollection[rowConfigKey].forEach((seatRow: SeatRowInterface) => {
          let seatArray = seatRow.seatArray;
          let firstSeat = seatArray[0];
          let lastSeat = seatArray[seatArray.length - 1];
          // Add helper attribute to each seatRow
          Object.assign(seatRow, {
            rowPosition: firstSeat.seatNumberDigit, // may need to change name
            isWing: firstSeat.isWing,
            isFirstWing: _.includes(firstSeat.seatIndicators, 'FIRSTWING'),
            isExitLeft: firstSeat.nextToExit,
            isExitRight: lastSeat.nextToExit,
            isRichSeatmap: model.rowsInfo.isRichSeatmap,
          });

          seatArray.forEach((seatModel: SeatInterface) => {
            processSeats(model, seatModel, data);
          });

          for (let i = 0; i < seatArray.length - 1; i++) {
            if (seatArray[i].aisle && seatArray[i + 1].aisle) {
              seatArray[i].aisleOnRightSide = true;
            }
          }
        });
      });
    },

    setActiveDeck: (
      state,
      action: PayloadAction<{
        activeDeck: string;
        activePax: PassengerInterface;
      }>
    ) => {
      let activeDeck = action.payload.activeDeck;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      let activePax = action.payload.activePax;
      let model = state.model;
      const activeSeatConfigId = model.rowsInfo.activeSeatConfigIds[model.activePaxId];

      let activeSeatmap = model.rows[model.seatmapForOperatingType][activeDeck];
      let seatmapArray = mapSeatArray(activeSeatmap, activeSeatConfigId);
      model.rowsAsCollection = seatmapArray;
      model.currentDeckUpper = activeDeck === 'U';
      processSeatmapCollection();
    },
    setSeatmapPaxAttribute: (
      state,
      action: PayloadAction<{ paxId: number; key: string; value: any }>
    ) => {
      let paxId = action.payload.paxId;
      const key = action.payload.key;
      const value = action.payload.value;
      let activeFlightPaxes = getActiveFlightPaxes(state.model);
      let foundPax = _.find(activeFlightPaxes, { paxIdReference: paxId });
      if (foundPax) {
        Object.assign(foundPax, { [key]: value });
      }
    },
    setExitSeatModalAttribute: (
      state,
      action: PayloadAction<{ isOpen: boolean; seatToChoose?: any }>
    ) => {
      state.exitSeatModal.isOpen = action.payload.isOpen;
      if (action.payload.seatToChoose) {
        state.exitSeatModal.seatToChoose = action.payload.seatToChoose;
      }
    },
  },
});

export const {
  setSeatmapModel,
  setSeatmapModelAttribute,
  setActivePax,
  resetSeatmapModel,
  changeSeatStatus,
  changePaxSeat,
  setRowConfigs,
  setSeatmapWorkingState,
  setActiveDeck,
  setExitSeatModalAttribute,
  setSeatmapPaxAttribute,
  processSeatmapCollection,
} = seatmapSlice.actions;

export const selectSeatmapModel = (state: RootState) => state.seatmap.model;
export const selectExitSeatModal = (state: RootState) => state.seatmap.exitSeatModal;
export default seatmapSlice.reducer;

function mapSeatArray(activeSeatmap: SeatmapInterface, activeSeatConfigId: number) {
  let seatmapArray: any[] = [];
  if (!activeSeatmap) return seatmapArray;

  const seatmap: any =
    activeSeatmap[activeSeatConfigId] ?? activeSeatmap[Object.keys(activeSeatmap)[0]];

  if (!seatmap) return seatmapArray;

  let rowsArray: any = [];
  Object.values(seatmap).forEach((item: any) => {
    rowsArray.push({ seatArray: item });
  });
  seatmapArray = rowsArray;

  return seatmapArray.filter((item): item is any => item !== null);
}

function processSeats(model: SeatmapModelInterface, seatModel: SeatInterface, data: any) {
  let seatStatus = seatModel.status,
    computedStatus = seatStatus,
    disabled = false,
    noWindowSeats = ['38A', '38F', '39F'],
    noWindowSeatPlaneTypes = ['a320-max', 'a321-max'];

  let facilityId = `${seatModel.rowPosition}_${seatModel.seatNumber}`;
  if (seatModel.isToilet) {
    if (!model.facilityCollection['toilet'].includes(facilityId)) {
      model.facilityCollection['toilet'].push(facilityId);
    }
  }
  if (seatModel.isGalley) {
    if (!model.facilityCollection['galley'].includes(facilityId)) {
      model.facilityCollection['galley'].push(facilityId);
    }
  }
  if (!_.isNull(seatModel.seatIndicators) && seatModel.seatIndicators.includes('AISLE')) {
    seatModel.aisle = true;
  }

  switch (data.seatmapMode) {
    case 'normal':
      // nothing to see here
      break;
    case 'infant':
      if (seatStatus === 'available' && !seatModel.infant) {
        computedStatus = 'occupied';
        disabled = true;
      }
      break;
    case 'child':
      if (seatStatus === 'available' && !seatModel.child) {
        computedStatus = 'occupied';
        disabled = true;
      }
      break;
    case 'disabled':
      if (seatStatus === 'available') {
        computedStatus = 'available-disabled';
        disabled = true;
      }
      break;
  }

  if (
    seatStatus !== 'available' &&
    !_.includes(data.assignedSeatReferences, seatModel.seatNumber)
  ) {
    disabled = true;
  }

  // Per-pax seat availability from seatAvailabilityDetails
  const activePaxId = model.activePaxId;
  const seatAvailabilityConfigId = seatModel.seatAvailabilityConfigId;

  const seatAvailabilityByConfig = _.isNil(seatAvailabilityConfigId)
    ? null
    : model.seatAvailabilityInfo?.[seatAvailabilityConfigId];
  const paxSeatAvailability = _.isNil(activePaxId) ? null : seatAvailabilityByConfig?.[activePaxId];
  const isActivePaxInitialSeat =
    !!data.activePaxInitialSeat && seatModel.seatNumber === data.activePaxInitialSeat;

  if (
    seatStatus === 'available' &&
    paxSeatAvailability?.seatAvailable === false &&
    !isActivePaxInitialSeat
  ) {
    computedStatus = 'available-disabled';
    disabled = true;
  }

  if (seatStatus === 'locked' && !_.includes(data.assignedSeatReferences, seatModel.seatNumber)) {
    let facilityId = `${seatModel.rowPosition}_${seatModel.seatNumber}`;
    if (seatModel.isToilet) {
      if (!model.facilityCollection['toilet'].includes(facilityId)) {
        model.facilityCollection['toilet'].push(facilityId);
      }
    }
    if (seatModel.isGalley) {
      if (!model.facilityCollection['galley'].includes(facilityId)) {
        model.facilityCollection['galley'].push(facilityId);
      }
    }
    if (!_.isNull(seatModel.seatIndicators) && seatModel.seatIndicators.includes('AISLE')) {
      seatModel.aisle = true;
    }

    disabled = false;
    computedStatus = 'available';
  }

  // Keep pax-specific seat eligibility after seat state transitions
  if (
    computedStatus === 'available' &&
    paxSeatAvailability?.seatAvailable === false &&
    !isActivePaxInitialSeat
  ) {
    disabled = true;
    computedStatus = 'available-disabled';
  }

  if (_.includes(data.assignedSeatReferences, seatModel.seatNumber) && !model.readOnly) {
    disabled = false;
    computedStatus = 'active';
  }

  // Seatmap from different operating class
  if (seatModel.seatOperatingClass !== model.seatmapForOperatingType) {
    computedStatus = 'unavailable';
    disabled = true;
  }

  // check plane types ( only A-320-max and A-321-max should have this )
  if (_.includes(noWindowSeatPlaneTypes, model.rowsInfo.seatmapClass)) {
    // if any of the seat numbers match this is no window seat
    if (_.includes(noWindowSeats, seatModel.seatNumber)) {
      seatModel.noWindowSeat = true;
    }
  }

  Object.assign(seatModel, {
    seatOccupied: computedStatus === 'occupied',
    seatAssigned: computedStatus === 'active',
    computedStatus: computedStatus,
    disabled: disabled,
    isRichSeatmap: model.rowsInfo.seatmapClass,
    tmpSeatNumber: seatModel.seatNumber.toLowerCase().replace(/^\s*[0]/g, ''),
  });
}

function getSeatmapDataObject(model: SeatmapModelInterface) {
  let activeFlightPaxes = getActiveFlightPaxes(model);
  let activePax = getCurrentlySelectedPax(activeFlightPaxes);
  return {
    assignedSeatReferences: _.map(
      getPaxesEligibleForSeatChange(activeFlightPaxes),
      'selectedSeatReference'
    ),
    activePaxInitialSeat: activePax?.initialSeat || '',
    seatmapMode: model.seatmapMode,
  };
}
